import React, { useState } from 'react';
import { Alert } from '../../../modules/ui-kit-react/lib';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTenantQuery } from 'components/Auth/AuthenticatedQueryProvider';
import TextInput from 'components/Form/TextInput';
// import MultiSelectInput from 'components/Form/MultiSelectInput';
import SelectInput from 'components/Form/SelectInput';
import { humanize } from 'utils/enums';
import Checkbox from 'components/Form/Checkbox';

interface EksCluster {
  id: number;
  name: string;
  region: string;
  awsProfile?: {
    name: string;
  }
}

interface EksHelmConfiguration {
  chartRepositoryUrl?: string;
  chartName?: string;
  releaseName?: string;
  valuesPath?: string;
  eksClusterId?: number;
  timeout?: number;
  namespace?: string;
  chartType?: 'http' | 'oci';
  chartVersion?: string;
  isEcr?: boolean;
  ecrHost?: string;
  // valuesFileUrls intentionally omitted from the form for now to avoid type issues
  valuesRepoUrl?: string;
  valuesRef?: string;
  releaseChart?: string;
  privateRepository?: boolean;
}

interface Props {
  eksHelmConfiguration?: EksHelmConfiguration;
}

function NoClusters() {
  const { tenant } = useParams();

  return (
    <Alert
      type="danger"
      message={(
        <span>
          We were unable to find any EKS clusters. In order to create an EKS
          deployment configuration, you will need to
          <Link to={`/${tenant}/admin/aws-profiles`}>
            sync the clusters from an AWS profile.
          </Link>
          <br />
          <br />
          You can read more about this process
          <a
            href="https://shield-docs.rvohealth.dev/docs/admin/profiles.html#syncing-eks-clusters"
            target="_blank"
            rel="noreferrer noopener"
          >
            here
          </a>
          .
        </span>
      )}
    />
  );
}

export default function EksHelmFields({ eksHelmConfiguration, gqlError }: Props & { gqlError?: any }) {
  // const [clusterId, setClusterId] = useState(eksHelmConfiguration?.cluster?.id);
  // Hooks must be declared before any early returns
  const [chartType, setChartType] = useState<'http' | 'oci'>(
    (eksHelmConfiguration?.chartType as any) || 'http'
  );

  const { data, error, isLoading } = useQuery<EksCluster[]>(
    useTenantQuery(['providers/aws/eks-clusters'])
  );

  if (isLoading) return null;
  if (error) return <Alert message={error.message} type="danger" />;
  const eksClusters = data || [];
  const noEksClusters = !eksClusters.length;

  return (
    <>
      {noEksClusters && <NoClusters />}

      <TextInput
        name="providerConfiguration[__typename]"
        type="hidden"
        defaultValue="eks-helm"
        disabled={noEksClusters}
      />

      {/* Chart Type */}
      <SelectInput
        name="providerConfiguration[chartType]"
        label="Chart Type"
        defaultValue={chartType}
        source={[{ value: 'http', label: 'HTTP' }, { value: 'oci', label: 'OCI' }]}
        onChange={(v: any) => setChartType(v as 'http' | 'oci')}
        disabled={noEksClusters}
      />

      {/* Chart Version (optional) */}
      <TextInput
        name="providerConfiguration[chartVersion]"
        label="Chart Version (optional)"
        defaultValue={eksHelmConfiguration?.chartVersion}
        disabled={noEksClusters}
        error={''}
      />

      {chartType === 'http' && (
        <>
          <TextInput
            name="providerConfiguration[chartRepositoryUrl]"
            label="Chart Repository URL"
            defaultValue={eksHelmConfiguration?.chartRepositoryUrl}
            disabled={noEksClusters}
            required
            error={''}
          />
          <TextInput
            name="providerConfiguration[chartName]"
            label="Chart Name"
            defaultValue={eksHelmConfiguration?.chartName}
            disabled={noEksClusters}
            required
            error={''}
          />
        </>
      )}

      {chartType === 'oci' && (
        <>
          <TextInput
            name="providerConfiguration[chartRepositoryUrl]"
            label="OCI Release (oci://...)"
            defaultValue={eksHelmConfiguration?.chartRepositoryUrl}
            disabled={noEksClusters}
            required
            error={''}
          />
          <Checkbox
            name="providerConfiguration[isEcr]"
            label="Chart is hosted in ECR"
            defaultValue={Boolean(eksHelmConfiguration?.isEcr)}
          />
          <TextInput
            name="providerConfiguration[ecrHost]"
            label="ECR Host (e.g., 123456789012.dkr.ecr.us-east-1.amazonaws.com)"
            defaultValue={eksHelmConfiguration?.ecrHost}
            disabled={noEksClusters}
            error={''}
          />
        </>
      )}

      {/* Private repository (applies to both HTTP and OCI) */}
      <Checkbox
        name="providerConfiguration[privateRepository]"
        label="Private repository (requires auth)"
        defaultValue={Boolean(eksHelmConfiguration?.privateRepository)}
      />

      {/* For OCI charts, hide chartName input but keep field present to maintain back-compat */}
      {chartType === 'oci' && (
        <TextInput
          name="providerConfiguration[chartName]"
          type="hidden"
          defaultValue={eksHelmConfiguration?.chartName || ''}
          error={''}
        />
      )}

      {/* Values source: always the project repo; only path needed */}
      <TextInput
        name="providerConfiguration[valuesPath]"
        label="Values Path (relative to repo root)"
        defaultValue={eksHelmConfiguration?.valuesPath}
        disabled={noEksClusters}
        error={''}
      />
      {/* Values Ref is derived from the selected deploy version (artifactRef); field removed */}

      <TextInput
        name="providerConfiguration[releaseName]"
        label="Release Name"
        defaultValue={eksHelmConfiguration?.releaseName}
        disabled={noEksClusters}
      />

      <SelectInput
        name="providerConfiguration[eksClusterId]"
        label="EKS Cluster"
        defaultValue={eksHelmConfiguration?.eksClusterId}
        source={eksClusters.map(cluster => ({
          value: cluster.id,
          label: `${cluster.awsProfile?.name || ''} - ${cluster.name} (${humanize(
            cluster.region
          )})`
        }))}
        required
      />

      <TextInput
        name="providerConfiguration[namespace]"
        label="Namespace"
        defaultValue={eksHelmConfiguration?.namespace}
        disabled={noEksClusters}
        required
        error={''}
      />

      <SelectInput
        name="providerConfiguration[timeout]"
        label="Timeout (min)"
        defaultValue={eksHelmConfiguration?.timeout || 600}
        source={[5, 10, 15, 30, 45, 60].map(minutes => ({
          value: minutes * 60,
          label: minutes.toString()
        }))}
        required
      />

    </>
  );
}